import { useState, useRef } from 'react';
import { BookOpen, Mail, KeyRound, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Login({ onLogin }) {
  const [step, setStep] = useState('email'); // email | code
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const codeRef = useRef(null);

  const requestCode = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/auth/request-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(typeof data.detail === 'string' ? data.detail : 'Could not send code'); return; }
      setStep('code'); setCode('');
      toast.success('Login code sent — check your email');
      setTimeout(() => codeRef.current?.focus(), 100);
    } catch { setError('Network error — please try again'); }
    finally { setLoading(false); }
  };

  const verifyCode = async (e) => {
    e?.preventDefault();
    if (code.trim().length !== 6) { setError('Enter the 6-digit code'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/auth/verify-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(typeof data.detail === 'string' ? data.detail : 'Verification failed'); return; }
      localStorage.setItem('token', data.token);
      onLogin(data.user);
    } catch { setError('Network error — please try again'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#101C47] flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center"><BookOpen size={20} className="text-white" /></div>
          <div>
            <h1 className="text-white font-bold text-xl leading-tight">Appetite Search</h1>
            <p className="text-white/40 text-xs">AI-powered underwriting guideline search</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-6">
          {step === 'email' ? (
            <form onSubmit={requestCode} className="space-y-4">
              <div>
                <h2 className="text-base font-bold text-gray-900">Sign in</h2>
                <p className="text-xs text-gray-400 mt-0.5">We'll email you a one-time login code — no password needed.</p>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-500 block mb-1">Email address</label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com"
                    className="h-10 text-sm pl-9" autoFocus data-testid="login-email-input" />
                </div>
              </div>
              {error && <p className="text-xs text-red-600" data-testid="login-error">{error}</p>}
              <Button type="submit" disabled={loading || !email.trim()} className="w-full h-10 text-sm bg-[#101C47] hover:bg-[#1a2a5e]" data-testid="login-request-code-btn">
                {loading ? 'Sending code…' : 'Email me a login code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={verifyCode} className="space-y-4">
              <div>
                <h2 className="text-base font-bold text-gray-900">Enter your code</h2>
                <p className="text-xs text-gray-400 mt-0.5">We sent a 6-digit code to <span className="font-semibold text-gray-600">{email}</span>. It expires in 10 minutes.</p>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-500 block mb-1">6-digit code</label>
                <div className="relative">
                  <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                  <Input ref={codeRef} inputMode="numeric" maxLength={6} value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, ''))} placeholder="••••••"
                    className="h-11 pl-9 text-lg tracking-[0.5em] font-bold" data-testid="login-code-input" />
                </div>
              </div>
              {error && <p className="text-xs text-red-600" data-testid="login-error">{error}</p>}
              <Button type="submit" disabled={loading || code.length !== 6} className="w-full h-10 text-sm bg-[#101C47] hover:bg-[#1a2a5e]" data-testid="login-verify-btn">
                {loading ? 'Verifying…' : 'Sign in'}
              </Button>
              <div className="flex items-center justify-between pt-1">
                <button type="button" onClick={() => { setStep('email'); setError(''); }} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1" data-testid="login-back-btn">
                  <ArrowLeft size={11} /> Change email
                </button>
                <button type="button" onClick={requestCode} disabled={loading} className="text-xs text-[#101C47] font-semibold hover:underline" data-testid="login-resend-btn">
                  Resend code
                </button>
              </div>
            </form>
          )}
        </div>
        <p className="text-white/25 text-[10px] text-center mt-6">Total Quality Insurance — internal tool</p>
      </div>
    </div>
  );
}
